import React, { useState } from 'react';
import { Play, Star, MapPin, ArrowRight, Quote } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import { DEFAULT_VIDEO_REVIEWS } from '../../constants/videoReviews';

function gridClassForCount(count) {
  if (count <= 2) return 'grid-cols-1 sm:grid-cols-2 max-w-3xl mx-auto';
  if (count === 3) return 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3';
  if (count === 5) return 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5';
  return 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4';
}

export default function CustomerVideoReviews({ reviews: reviewsProp }) {
  const reviews = (reviewsProp && reviewsProp.length > 0) ? reviewsProp : DEFAULT_VIDEO_REVIEWS;
  const [activeId, setActiveId] = useState(null);
  const active = reviews.find((r) => r.id === activeId) || null;

  return (
    <section className="bg-gradient-to-b from-brand-grey/70 via-white to-white border-b border-brand-greyDark">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-5 sm:pt-7 pb-6 sm:pb-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-3 mb-4 sm:mb-5">
          <div className="flex items-start gap-3">
            <div className="hidden sm:flex w-10 h-10 rounded-xl bg-brand-orange/10 text-brand-orange items-center justify-center shrink-0">
              <Quote size={18} fill="currentColor" />
            </div>
            <div>
              <div className="inline-flex items-center gap-1.5 text-brand-orange text-[10.5px] sm:text-[11px] font-bold uppercase tracking-[0.18em] mb-1">
                <Star size={11} className="fill-brand-orange text-brand-orange" />
                <span>4.9 / 5 from 2,000+ verified travellers</span>
              </div>
              <h2 className="font-poppins text-[18px] sm:text-[22px] lg:text-[26px] text-brand-blue font-extrabold leading-tight">
                What Our Travellers Say After Their Trip
                <span className="hidden sm:inline text-brand-blue/40 font-medium text-[15px] lg:text-[17px] ml-2">- in their own words</span>
              </h2>
            </div>
          </div>
          <a
            href="https://www.youtube.com/@mountainschaintravel/shorts"
            target="_blank"
            rel="noopener noreferrer"
            className="hidden sm:inline-flex items-center gap-1.5 text-brand-orange font-semibold text-[12.5px] hover:gap-2.5 transition-all self-start md:self-auto shrink-0"
          >
            Watch all reviews <ArrowRight size={14} />
          </a>
        </div>

        <div className={`grid gap-3 sm:gap-4 ${gridClassForCount(reviews.length)}`}>
          {reviews.map((r) => (
            <ReviewCard
              key={r.id}
              review={r}
              onPlay={() => setActiveId(r.id)}
            />
          ))}
        </div>

        <div className="sm:hidden mt-4 text-center">
          <a
            href="https://www.youtube.com/@mountainschaintravel/shorts"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-brand-orange font-semibold text-[13px]"
          >
            Watch all reviews <ArrowRight size={14} />
          </a>
        </div>
      </div>

      <Dialog open={Boolean(active)} onOpenChange={(open) => { if (!open) setActiveId(null); }}>
        <DialogContent
          className="p-0 border-0 bg-transparent shadow-none max-w-5xl w-[95vw] sm:w-[90vw]"
        >
          {active && (
            <div className="bg-brand-blue rounded-2xl overflow-hidden shadow-premium ring-1 ring-white/10">
              <div className="relative aspect-video bg-black">
                <iframe
                  key={active.id}
                  src={`https://www.youtube.com/embed/${active.id}?autoplay=1&rel=0&modestbranding=1&playsinline=1`}
                  title={`${active.name} - ${active.destination}`}
                  className="absolute inset-0 w-full h-full border-0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
              <div className="px-5 sm:px-7 py-4 sm:py-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="min-w-0">
                  <DialogTitle className="text-white font-poppins font-extrabold text-[17px] sm:text-[19px] leading-tight truncate">
                    {active.name} · {active.destination}
                  </DialogTitle>
                  <DialogDescription className="text-white/75 text-[13px] sm:text-[14px] mt-1 leading-snug line-clamp-2">
                    “{active.quote}”
                  </DialogDescription>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <div className="flex items-center gap-0.5">
                    {[...Array(active.rating)].map((_, j) => (
                      <Star key={j} size={14} className="fill-brand-orange text-brand-orange" />
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={() => setActiveId(null)}
                    className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white text-[12.5px] font-semibold transition-colors"
                    aria-label="Close video"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}

function ReviewCard({ review, onPlay }) {
  return (
    <div className="group rounded-2xl overflow-hidden bg-white border border-brand-greyDark shadow-card card-hover">
      <div className="relative aspect-video bg-brand-blue">
        <button
          type="button"
          onClick={onPlay}
          aria-label={`Play review from ${review.name}`}
          className="absolute inset-0 w-full h-full cursor-pointer"
        >
          <img
            src={`https://img.youtube.com/vi/${review.id}/hqdefault.jpg`}
            alt={review.name}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-brand-blue/85 via-brand-blue/20 to-transparent" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-white/95 text-brand-orange shadow-2xl flex items-center justify-center group-hover:scale-110 transition-transform">
              <Play size={22} className="ml-0.5 fill-brand-orange" />
            </div>
          </div>
          <div className="absolute top-2.5 left-2.5 flex items-center gap-0.5 bg-black/55 backdrop-blur-sm px-1.5 py-0.5 rounded">
            {[...Array(review.rating)].map((_, j) => (
              <Star key={j} size={9} className="fill-yellow-400 text-yellow-400" />
            ))}
          </div>
          <div className="absolute bottom-0 left-0 right-0 p-2.5 sm:p-3 text-left">
            <div className="flex items-center gap-1 text-white/85 text-[10.5px] sm:text-[11px] font-semibold uppercase tracking-wider">
              <MapPin size={10} className="text-brand-orange" /> {review.destination}
            </div>
          </div>
        </button>
      </div>
      <div className="px-3 py-2 sm:px-3.5 sm:py-2.5 flex items-center justify-between gap-2 bg-white">
        <div className="min-w-0">
          <div className="text-brand-blue font-semibold text-[12.5px] sm:text-[13px] truncate">{review.name}</div>
          <div className="text-brand-blue/55 text-[10.5px] truncate">Verified traveller · Mountains Chain</div>
        </div>
        <div className="flex items-center gap-0.5 shrink-0">
          {[...Array(review.rating)].map((_, j) => (
            <Star key={j} size={11} className="fill-brand-orange text-brand-orange" />
          ))}
        </div>
      </div>
    </div>
  );
}
